// ─── Local completion markers for the /learn to-do model ───────────────────────
//
// The dashboard's truth is the server (flare sessions, onboarding capture, test
// sessions, plan). These markers are the fallback for the gap between a page
// finishing and the server reflecting it — the Flare's single POST is
// fire-and-forget, an interview finalize can lag, and a dev stack may not carry
// the newer read endpoints yet. A marker never *un*-completes anything; the
// merge rule in useLearnProgress is `server || local`.
//
// Keyed per user so a shared browser never shows one learner another's state.

const PREFIX = 'novara_learn_progress_v1';

export const PROGRESS_KEYS = {
  flare: 'flare',
  profile: 'profile',
  interview: 'interview',
  fc: 'fc',
};

// The signed-in user's id as AuthContext persists it (sessionStorage 'user'),
// so a finishing page can drop a marker without pulling in the auth context.
export function currentUserId() {
  try {
    const raw = window.sessionStorage.getItem('user');
    const u = raw ? JSON.parse(raw) : null;
    return u && u.id != null ? u.id : null;
  } catch {
    return null;
  }
}

function storageKey(userId) {
  const id = userId === undefined ? currentUserId() : userId;
  return `${PREFIX}:${id == null ? 'anon' : id}`;
}

export function readProgress(userId) {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function markDone(userId, key, extra = {}) {
  try {
    const cur = readProgress(userId);
    cur[key] = { done: true, at: new Date().toISOString(), ...extra };
    window.localStorage.setItem(storageKey(userId), JSON.stringify(cur));
  } catch {
    /* storage disabled — the server remains the source of truth */
  }
}

export function isDone(userId, key) {
  const cur = readProgress(userId);
  return Boolean(cur[key] && cur[key].done);
}

export function localExtra(userId, key) {
  const cur = readProgress(userId);
  return (cur[key] && cur[key].done && cur[key]) || null;
}

// Test/dev helper — never called from product code.
export function clearProgress(userId) {
  try { window.localStorage.removeItem(storageKey(userId)); } catch { /* noop */ }
}

// Clear this browser's markers for a user (or every user when no id is given).
// The dashboard reads "server OR local marker", so an admin progress reset that
// only clears the server would leave the to-dos ticked in the learner's browser.
export function clearLocalProgress(userId) {
  try {
    const keys = Object.keys(window.localStorage).filter((k) => k.startsWith(PREFIX));
    const target = userId == null ? null : storageKey(userId);
    keys.filter((k) => target == null || k === target)
      .forEach((k) => window.localStorage.removeItem(k));
  } catch (e) {
    /* private mode / storage disabled — the server reset still stands */
  }
}
